// 微信公众号认证服务
// 实现微信公众号OAuth2.0认证流程

export interface WechatUser {
    openid: string;
    nickname: string;
    sex: number; // 1为男性，2为女性，0为未知
    province: string;
    city: string;
    country: string;
    headimgurl: string;
    privilege: string[];
    unionid?: string;
}

export interface WechatAuthConfig {
    appId: string;
    redirectUri: string;
    scope: 'snsapi_base' | 'snsapi_userinfo';
    state?: string;
}

export interface WechatTokenResponse {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    openid: string;
    scope: string;
    unionid?: string;
}

export interface WechatAuthResult {
    success: boolean;
    user?: WechatUser;
    token?: string;
    error?: string;
}

class WechatAuthService {
    private readonly API_BASE_URL = '/api/wechat';
    private readonly WECHAT_AUTH_URL = 'https://open.weixin.qq.com/connect/oauth2/authorize';

    private config: WechatAuthConfig;
    private currentUser: WechatUser | null = null;
    private accessToken: string | null = null;
    private isAuthenticated: boolean = false;

    constructor() {
        this.config = {
            appId: process.env.REACT_APP_WECHAT_APP_ID || '',
            redirectUri: process.env.REACT_APP_WECHAT_REDIRECT_URI || window.location.origin + '/wechat/callback',
            scope: 'snsapi_userinfo',
            state: this.generateState()
        };

        // 从localStorage恢复认证状态
        this.restoreAuthState();
    }

    /**
     * 生成随机state参数，防止CSRF攻击
     */
    private generateState(): string {
        return Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
    }

    /**
     * 从localStorage恢复认证状态
     */
    private restoreAuthState(): void {
        try {
            const savedUser = localStorage.getItem('wechat_user');
            const savedToken = localStorage.getItem('wechat_token');
            const savedExpiry = localStorage.getItem('wechat_token_expiry');

            if (savedUser && savedToken && savedExpiry) {
                const expiry = parseInt(savedExpiry);
                if (Date.now() < expiry) {
                    this.currentUser = JSON.parse(savedUser);
                    this.accessToken = savedToken;
                    this.isAuthenticated = true;
                    console.log('微信认证状态已恢复');
                } else {
                    // Token已过期，清理状态
                    this.clearAuthState();
                }
            }
        } catch (error) {
            console.error('恢复微信认证状态失败:', error);
            this.clearAuthState();
        }
    }

    /**
     * 保存认证状态到localStorage
     */
    private saveAuthState(user: WechatUser, token: string, expiresIn: number): void {
        try {
            const expiry = Date.now() + (expiresIn * 1000);
            localStorage.setItem('wechat_user', JSON.stringify(user));
            localStorage.setItem('wechat_token', token);
            localStorage.setItem('wechat_token_expiry', expiry.toString());
        } catch (error) {
            console.error('保存微信认证状态失败:', error);
        }
    }

    /**
     * 清理认证状态
     */
    private clearAuthState(): void {
        this.currentUser = null;
        this.accessToken = null;
        this.isAuthenticated = false;

        localStorage.removeItem('wechat_user');
        localStorage.removeItem('wechat_token');
        localStorage.removeItem('wechat_token_expiry');
        localStorage.removeItem('wechat_state');
    }

    /**
     * 检查是否在微信浏览器中
     */
    isWechatBrowser(): boolean {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('micromessenger');
    }

    /**
     * 获取微信授权URL
     */
    getAuthUrl(): string {
        const state = this.generateState();
        localStorage.setItem('wechat_state', state);

        const params = new URLSearchParams({
            appid: this.config.appId,
            redirect_uri: encodeURIComponent(this.config.redirectUri),
            response_type: 'code',
            scope: this.config.scope,
            state: state
        });

        return `${this.WECHAT_AUTH_URL}?${params.toString()}#wechat_redirect`;
    }

    /**
     * 处理微信回调，获取授权码并换取用户信息
     */
    async handleCallback(code: string, state: string): Promise<WechatAuthResult> {
        try {
            // 验证state参数
            const savedState = localStorage.getItem('wechat_state');
            if (!savedState || savedState !== state) {
                throw new Error('State参数验证失败，可能存在安全风险');
            }

            console.log('处理微信回调，授权码:', code);

            // 调用后端API换取用户信息
            const response = await fetch(`${this.API_BASE_URL}/auth`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ code, state })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`认证失败: ${response.status} - ${errorText}`);
            }

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || '认证失败');
            }

            // 保存用户信息和认证状态
            this.currentUser = result.user;
            this.accessToken = result.token;
            this.isAuthenticated = true;

            // 保存到localStorage
            this.saveAuthState(result.user, result.token, result.expiresIn || 7200);

            // 清理state
            localStorage.removeItem('wechat_state');

            console.log('微信认证成功:', this.currentUser);

            return {
                success: true,
                user: this.currentUser || undefined,
                token: this.accessToken || undefined
            };

        } catch (error) {
            console.error('微信认证失败:', error);
            this.clearAuthState();

            return {
                success: false,
                error: error instanceof Error ? error.message : '认证失败'
            };
        }
    }

    /**
     * 启动微信认证流程
     */
    async authorize(): Promise<void> {
        if (!this.config.appId) {
            throw new Error('微信AppID未配置，请检查环境变量REACT_APP_WECHAT_APP_ID');
        }

        if (!this.isWechatBrowser()) {
            throw new Error('请在微信中打开此页面');
        }

        const authUrl = this.getAuthUrl();
        console.log('跳转到微信授权页面:', authUrl);

        // 跳转到微信授权页面
        window.location.href = authUrl;
    }

    /**
     * 检查认证状态
     */
    async checkAuthStatus(): Promise<boolean> {
        if (this.isAuthenticated && this.currentUser) {
            // 验证token是否仍然有效
            try {
                const response = await fetch(`${this.API_BASE_URL}/verify`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${this.accessToken}`,
                        'Accept': 'application/json'
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    const result = await response.json();
                    return result.valid === true;
                } else {
                    // Token无效，清理状态
                    this.clearAuthState();
                    return false;
                }
            } catch (error) {
                console.error('验证认证状态失败:', error);
                return this.isAuthenticated;
            }
        }

        return false;
    }

    /**
     * 获取当前用户信息
     */
    getCurrentUser(): WechatUser | null {
        return this.currentUser;
    }

    /**
     * 获取访问令牌
     */
    getAccessToken(): string | null {
        return this.accessToken;
    }

    /**
     * 检查是否已认证
     */
    getIsAuthenticated(): boolean {
        return this.isAuthenticated;
    }

    /**
     * 登出
     */
    async logout(): Promise<void> {
        try {
            // 调用后端登出接口
            if (this.accessToken) {
                await fetch(`${this.API_BASE_URL}/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${this.accessToken}`,
                        'Accept': 'application/json'
                    },
                    credentials: 'include'
                });
            }
        } catch (error) {
            console.error('调用登出接口失败:', error);
        } finally {
            // 无论后端调用是否成功，都清理本地状态
            this.clearAuthState();
            console.log('微信登出成功');
        }
    }

    /**
     * 刷新访问令牌
     */
    async refreshToken(): Promise<boolean> {
        try {
            const response = await fetch(`${this.API_BASE_URL}/refresh`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.accessToken}`,
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('刷新令牌失败');
            }

            const result = await response.json();

            if (result.success) {
                this.accessToken = result.token;
                // 更新localStorage中的token
                localStorage.setItem('wechat_token', result.token);
                const expiry = Date.now() + (result.expiresIn || 7200) * 1000;
                localStorage.setItem('wechat_token_expiry', expiry.toString());

                return true;
            } else {
                throw new Error(result.message || '刷新令牌失败');
            }
        } catch (error) {
            console.error('刷新访问令牌失败:', error);
            this.clearAuthState();
            return false;
        }
    }

    /**
     * 健康检查
     */
    async healthCheck(): Promise<{ status: string; message: string }> {
        try {
            const response = await fetch(`${this.API_BASE_URL}/health`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();
                return {
                    status: 'ok',
                    message: result.message || '微信认证服务正常'
                };
            } else {
                return {
                    status: 'error',
                    message: `服务异常: ${response.status}`
                };
            }
        } catch (error) {
            return {
                status: 'error',
                message: `连接失败: ${error instanceof Error ? error.message : '未知错误'}`
            };
        }
    }
}

// 导出单例实例
export const wechatAuthService = new WechatAuthService();
export default wechatAuthService;
